import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Bell, Settings, CheckCircle2, Users, UserCircle, FileSpreadsheet,
  FileText, Ticket, Briefcase, Package, Calendar, CreditCard,
} from 'lucide-react';

function formatNotificationTime(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now - date;
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return date.toLocaleDateString();
}

const CATEGORY_ICONS = {
  lead: Users, client: UserCircle, invoice: FileSpreadsheet,
  quote: FileText, ticket: Ticket, job: Briefcase,
  user: Users, inventory: Package, hr: Users,
  schedule: Calendar, billing: CreditCard, system: Settings,
};

function NotificationItem({ notification, isRead, onMarkRead, onNavigate }) {
  const IconComponent = CATEGORY_ICONS[notification.category] || Bell;
  return (
    <div
      className={`p-4 hover:bg-muted/50 transition-colors cursor-pointer ${isRead ? 'opacity-75' : ''}`}
      onClick={() => {
        if (!isRead) onMarkRead(notification.id);
        if (notification.link) onNavigate(notification.link);
      }}
    >
      <div className="flex gap-3">
        <div className={`flex-shrink-0 h-9 w-9 rounded-full flex items-center justify-center ${isRead ? 'bg-muted' : 'bg-primary/10'}`}>
          <IconComponent className={`h-4 w-4 ${isRead ? 'text-muted-foreground' : 'text-primary'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{notification.title}</p>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notification.message}</p>
          <p className="text-xs text-muted-foreground/60 mt-1">{formatNotificationTime(notification.created_at)}</p>
        </div>
        {!isRead && <div className="flex-shrink-0"><div className="h-2 w-2 rounded-full bg-primary" /></div>}
      </div>
    </div>
  );
}

const EMPTY_IMG = "https://customer-assets.emergentagent.com/job_33825c2f-db5b-4b28-a324-6229dd156c11/artifacts/yl99ubq3_image.png";

export function NotificationPanel({
  open,
  onOpenChange,
  notifications,
  notificationCounts,
  notificationsLoading,
  onMarkAsRead,
  onMarkAllAsRead,
}) {
  const navigate = useNavigate();

  const handleNavigate = (link) => {
    navigate(link);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0" data-testid="notification-panel">
        <SheetHeader className="px-4 py-3 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SheetTitle className="text-lg font-semibold">Notifications</SheetTitle>
              {notificationCounts.unread > 0 && (
                <Badge variant="destructive" className="h-5 min-w-5 px-1.5 text-xs">{notificationCounts.unread}</Badge>
              )}
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="notification-settings-button">
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        <Tabs defaultValue="unread" className="w-full">
          <div className="px-4 py-2 border-b border-border flex items-center justify-between">
            <TabsList className="grid grid-cols-3 flex-1">
              <TabsTrigger value="unread" data-testid="notification-tab-unread">
                Unread {notifications.unread.length > 0 && `(${notifications.unread.length})`}
              </TabsTrigger>
              <TabsTrigger value="read" data-testid="notification-tab-read">Read</TabsTrigger>
              <TabsTrigger value="ai" data-testid="notification-tab-ai">AI</TabsTrigger>
            </TabsList>
          </div>

          {notifications.unread.length > 0 && (
            <div className="px-4 py-2 border-b border-border">
              <Button variant="ghost" size="sm" className="text-xs text-primary hover:text-primary" onClick={onMarkAllAsRead}>
                <CheckCircle2 className="h-3 w-3 mr-1" />Mark all as read
              </Button>
            </div>
          )}

          <ScrollArea className="h-[calc(100vh-180px)]">
            <TabsContent value="unread" className="m-0" data-testid="notification-content-unread">
              {notificationsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              ) : notifications.unread.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center px-4">
                  <img src={EMPTY_IMG} alt="No notifications" className="w-56 h-56 object-contain mb-4" />
                  <p className="text-sm font-medium text-foreground">No unread notifications</p>
                  <p className="text-xs text-muted-foreground mt-1">You're all caught up!</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {notifications.unread.map((n) => (
                    <NotificationItem key={n.id} notification={n} isRead={false} onMarkRead={onMarkAsRead} onNavigate={handleNavigate} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="read" className="m-0" data-testid="notification-content-read">
              {notificationsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              ) : notifications.read.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center px-4">
                  <img src={EMPTY_IMG} alt="No notifications" className="w-56 h-56 object-contain mb-4" />
                  <p className="text-sm font-medium text-foreground">No read notifications</p>
                  <p className="text-xs text-muted-foreground mt-1">Read notifications will appear here</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {notifications.read.map((n) => (
                    <NotificationItem key={n.id} notification={n} isRead onMarkRead={() => {}} onNavigate={handleNavigate} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="ai" className="m-0 p-4" data-testid="notification-content-ai">
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <img src={EMPTY_IMG} alt="No AI notifications" className="w-56 h-56 object-contain mb-4" />
                <p className="text-sm font-medium text-foreground">No AI notifications</p>
                <p className="text-xs text-muted-foreground mt-1">AI updates and insights will appear here</p>
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
